/**
 * AI recommendation ribbon copy — generation modes, context sources, prompt building.
 * Used by admin editor, server public API, preview bundle, and hybrid.js.
 */

export const AI_RECOMMENDATION_GENERATION_MODE = Object.freeze({
  /** Merchant/static copy from schema `text` only. */
  STATIC: "static",
  /** Generate when popup opens; keep schema text as fallback on failure. */
  FALLBACK: "fallback",
  /** Always generate when popup opens (no static fallback). */
  RUNTIME: "runtime",
});

export const AI_RECOMMENDATION_CONTEXT_SOURCE = Object.freeze({
  CART: "cart",
  CURRENT_PRODUCT: "currentProduct",
  OFFER_PRODUCT: "offerProduct",
  PAGE: "page",
  BROWSING: "browsing",
});

/** Common localStorage keys read when Browsing context is enabled and no keys are configured. */
export const DEFAULT_AI_RECOMMENDATION_STORAGE_KEYS = Object.freeze([
  "recently_viewed",
  "viewed_products",
  "_shopify_recently_viewed",
  "customer",
  "user",
  "shopify_recently_viewed",
]);

/** Set by hybrid.js after storefront pre-hydration so the popup bundle skips a duplicate API call. */
export const AI_RECOMMENDATION_RUNTIME_COPY_HYDRATED = "runtimeCopyHydrated";

/** Default merchant-facing pitch when AI generation is off (static mode). */
export const DEFAULT_AI_RECOMMENDATION_STATIC_TEXT =
  "Picked for you by AI, based on what shoppers actually buy";

export const DEFAULT_AI_RECOMMENDATION_STATIC_BANNER_TEXT =
  `AI RECOMMENDATION\n${DEFAULT_AI_RECOMMENDATION_STATIC_TEXT}`;

export const DEFAULT_AI_RECOMMENDATION_META = Object.freeze({
  /** Static by default — merchants opt in to AI generation in the editor. */
  generationMode: AI_RECOMMENDATION_GENERATION_MODE.STATIC,
  /** Explicit editor opt-in. Legacy templates with generationMode:"fallback" stay static until checked. */
  useAiGeneration: false,
  contextSources: [
    AI_RECOMMENDATION_CONTEXT_SOURCE.CART,
    AI_RECOMMENDATION_CONTEXT_SOURCE.CURRENT_PRODUCT,
    AI_RECOMMENDATION_CONTEXT_SOURCE.OFFER_PRODUCT,
    AI_RECOMMENDATION_CONTEXT_SOURCE.PAGE,
    AI_RECOMMENDATION_CONTEXT_SOURCE.BROWSING,
  ],
  storageKeys: [...DEFAULT_AI_RECOMMENDATION_STORAGE_KEYS],
  labelLine: "AI RECOMMENDATION",
  maxWords: 22,
  prompt:
    "Write a natural, conversational upsell pitch for this shopper. " +
    "Line 1 must be exactly: AI RECOMMENDATION. " +
    "Line 2: one short sentence that feels like a helpful store associate — not a generic ad. " +
    "If a shopper name is provided in context, start line 2 with \"Hi, {FirstName},\" (first name only). " +
    "Reference their cart, current product, page, or browsing signals when available. " +
    "Explain why this add-on fits right now. Stay under 22 words on line 2. Plain text only — no quotes or markdown.",
});

/**
 * Resolve generation mode: AI runs only after explicit editor opt-in.
 * Legacy templates shipped with generationMode:"fallback" stay static until the merchant checks the box.
 */
export function resolveAiRecommendationGenerationMode(aiMeta = {}) {
  const rawMode = String(aiMeta.generationMode || "").toLowerCase();
  const optedIn = aiMeta.useAiGeneration === true;

  if (optedIn) {
    if (rawMode === AI_RECOMMENDATION_GENERATION_MODE.RUNTIME) {
      return AI_RECOMMENDATION_GENERATION_MODE.RUNTIME;
    }
    return AI_RECOMMENDATION_GENERATION_MODE.FALLBACK;
  }

  if (rawMode === AI_RECOMMENDATION_GENERATION_MODE.RUNTIME) {
    return AI_RECOMMENDATION_GENERATION_MODE.RUNTIME;
  }

  return AI_RECOMMENDATION_GENERATION_MODE.STATIC;
}

const MAX_WORDS_LINE_2_CAP = 120;

/**
 * Parse word limits from merchant prompt text, e.g. "under 10 words".
 * @param {string} promptText
 * @returns {number | null}
 */
export function extractMaxWordsFromPrompt(promptText) {
  const text = String(promptText || "");
  const patterns = [
    /\b(?:under|below|max(?:imum)?|at most|no more than|limit(?:ed)? to|within)\s+(\d{1,3})\s+words?\b/i,
    /\b(\d{1,3})\s+words?\s+(?:on\s+)?line\s*2\b/i,
    /\bline\s*2[^.\n]{0,96}?(\d{1,3})\s+words?\b/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    const value = Number.parseInt(match[1], 10);
    if (Number.isFinite(value) && value >= 1 && value <= MAX_WORDS_LINE_2_CAP) {
      return value;
    }
  }
  return null;
}

/**
 * @param {object | null | undefined} aiMeta
 * @returns {number}
 */
export function resolveAiRecommendationMaxWords(aiMeta) {
  const fromPrompt = extractMaxWordsFromPrompt(aiMeta?.prompt);
  if (fromPrompt != null) return fromPrompt;

  const explicit = Number(aiMeta?.maxWords);
  if (Number.isFinite(explicit) && explicit >= 1) {
    return Math.min(Math.round(explicit), MAX_WORDS_LINE_2_CAP);
  }
  return DEFAULT_AI_RECOMMENDATION_META.maxWords;
}

/**
 * @param {object} aiMeta
 * @returns {string}
 */
export function buildAiRecommendationSystemPrompt(aiMeta) {
  const meta = { ...DEFAULT_AI_RECOMMENDATION_META, ...aiMeta };
  const maxWords = resolveAiRecommendationMaxWords(meta);
  const label = meta.labelLine || "AI RECOMMENDATION";
  return (
    "You are a Shopify upsell copywriter. Return exactly 2 lines of plain text. " +
    `Line 1 must be exactly: ${label}. ` +
    `Line 2 must be ONE sentence with at most ${maxWords} words — count carefully and never exceed this limit. ` +
    "Reference cart, page, or browsing context when provided. " +
    "If a shopper name is given, you may greet them by first name on line 2. " +
    "No JSON, no markdown, no bullets, no extra lines."
  );
}

function truncateToWordLimit(text, limit) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "";
  if (words.length <= limit) return words.join(" ");
  return words.slice(0, limit).join(" ");
}

/**
 * @param {object | null | undefined} section TEXT section (top-level aiBanner)
 */
/**
 * True when hybrid.js already fetched AI copy for this banner on the storefront.
 * @param {object | null | undefined} section
 */
export function isRuntimeAiCopyHydrated(section) {
  return section?.aiMeta?.[AI_RECOMMENDATION_RUNTIME_COPY_HYDRATED] === true;
}

export function getAiRecommendationMeta(section) {
  const aiMeta = section?.aiMeta && typeof section.aiMeta === "object" ? section.aiMeta : {};
  const display =
    section?.setting?.display && typeof section.setting.display === "object"
      ? section.setting.display
      : {};
  const mergedMeta = {
    ...DEFAULT_AI_RECOMMENDATION_META,
    ...display,
    ...aiMeta,
  };
  const generationMode = resolveAiRecommendationGenerationMode(mergedMeta);
  return {
    ...mergedMeta,
    generationMode,
    useAiGeneration: generationMode !== AI_RECOMMENDATION_GENERATION_MODE.STATIC,
    contextSources: normalizeContextSources(
      aiMeta.contextSources ?? display.contextSources ?? DEFAULT_AI_RECOMMENDATION_META.contextSources
    ),
    storageKeys: normalizeStorageKeys(
      aiMeta.storageKeys ?? display.storageKeys ?? DEFAULT_AI_RECOMMENDATION_META.storageKeys
    ),
    maxWords: resolveAiRecommendationMaxWords({
      prompt: aiMeta.prompt ?? display.prompt,
      maxWords: aiMeta.maxWords ?? display.maxWords,
    }),
  };
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeContextSources(raw) {
  const allowed = new Set(Object.values(AI_RECOMMENDATION_CONTEXT_SOURCE));
  const list = Array.isArray(raw) ? raw : [];
  const out = list.filter((k) => allowed.has(k));
  return out.length ? out : [...DEFAULT_AI_RECOMMENDATION_META.contextSources];
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeStorageKeys(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((k) => String(k || "").trim())
    .filter(Boolean)
    .slice(0, 8);
}

/**
 * @param {object} params
 * @param {object} params.aiMeta
 * @param {object} [params.context]
 * @param {object} [params.offerProduct]
 */
export function buildAiRecommendationPrompt({ aiMeta, context = {}, offerProduct }) {
  const meta = {
    ...DEFAULT_AI_RECOMMENDATION_META,
    ...aiMeta,
    maxWords: resolveAiRecommendationMaxWords({
      prompt: aiMeta?.prompt,
      maxWords: aiMeta?.maxWords,
    }),
  };
  const lines = [String(meta.prompt || DEFAULT_AI_RECOMMENDATION_META.prompt).trim()];

  if (offerProduct?.title) {
    lines.push(`Recommended product: ${offerProduct.title}.`);
  }

  const shopperName =
    context.shopperName || extractShopperDisplayName(context.browsing, context.shopper);
  if (shopperName) {
    lines.push(`Shopper name (use for greeting if appropriate): ${shopperName}.`);
  }

  const sources = normalizeContextSources(meta.contextSources);
  if (sources.includes(AI_RECOMMENDATION_CONTEXT_SOURCE.CART) && context.cart) {
    lines.push(`Cart context: ${summarizeCart(context.cart)}`);
  }
  if (
    sources.includes(AI_RECOMMENDATION_CONTEXT_SOURCE.CURRENT_PRODUCT) &&
    context.currentProduct
  ) {
    lines.push(`Current page product: ${summarizeProduct(context.currentProduct)}`);
  }
  if (
    sources.includes(AI_RECOMMENDATION_CONTEXT_SOURCE.OFFER_PRODUCT) &&
    (context.offerProduct || offerProduct)
  ) {
    lines.push(
      `Offer product: ${summarizeProduct(context.offerProduct || offerProduct)}`
    );
  }
  if (sources.includes(AI_RECOMMENDATION_CONTEXT_SOURCE.PAGE) && context.page) {
    const pageParts = [
      context.page.path || context.page.url || "unknown",
      context.page.title ? `title: ${context.page.title}` : "",
      context.page.referrer ? `referrer: ${context.page.referrer}` : "",
    ].filter(Boolean);
    lines.push(`Page context: ${pageParts.join(" · ")}`);
  }
  if (sources.includes(AI_RECOMMENDATION_CONTEXT_SOURCE.BROWSING) && context.browsing) {
    const browsing = JSON.stringify(context.browsing);
    if (browsing && browsing !== "{}") {
      lines.push(`Browsing signals (cookies, localStorage, session): ${browsing.slice(0, 1600)}`);
    }
  }

  lines.push(
    `Output format: line 1 must be exactly "${meta.labelLine || "AI RECOMMENDATION"}". ` +
      `Line 2 must be ONE sentence with at most ${meta.maxWords} words (hard limit). ` +
      "Plain text only — no quotes or markdown."
  );
  return lines.join("\n");
}

/**
 * Split AI pitch text into label + summary lines.
 * @param {string} raw
 * @returns {{ label: string, summary: string }}
 */
export function parseAiRibbonText(raw) {
  const text = String(raw || "").trim();
  if (!text) {
    return { label: "AI RECOMMENDATION", summary: "" };
  }

  const newline = text.indexOf("\n");
  if (newline >= 0) {
    return {
      label: text.slice(0, newline).trim(),
      summary: text.slice(newline + 1).trim(),
    };
  }

  const match = text.match(/^(AI\s+RECOMMENDATION)\s*[.:]?\s*(.*)$/i);
  if (match) {
    return {
      label: match[1].toUpperCase(),
      summary: match[2].trim(),
    };
  }

  return { label: "AI RECOMMENDATION", summary: text };
}

/**
 * Enforce label + single pitch line and word cap on line 2.
 * @param {string} raw
 * @param {string} [labelLine]
 * @param {number} [maxWords]
 */
export function clampAiRecommendationPitchLines(
  raw,
  labelLine = "AI RECOMMENDATION",
  maxWords = DEFAULT_AI_RECOMMENDATION_META.maxWords
) {
  const limit = Math.max(
    1,
    Math.min(
      Math.round(Number(maxWords)) || DEFAULT_AI_RECOMMENDATION_META.maxWords,
      MAX_WORDS_LINE_2_CAP
    )
  );
  const parsed = parseAiRibbonText(raw);
  const label = String(labelLine || parsed.label || "AI RECOMMENDATION").trim();
  let summary = String(parsed.summary || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!summary) {
    summary = String(raw || "")
      .replace(/^ai\s+recommendation\s*/i, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  summary = truncateToWordLimit(summary, limit);
  if (!summary) return label;
  return `${label}\n${summary}`;
}

/**
 * @param {string} raw
 * @param {string} [labelLine]
 * @param {number} [maxWords]
 */
export function formatAiRecommendationCopy(
  raw,
  labelLine = "AI RECOMMENDATION",
  maxWords
) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const limit = resolveAiRecommendationMaxWords({
    maxWords,
    labelLine,
  });
  return clampAiRecommendationPitchLines(text, labelLine, limit);
}

/**
 * @param {object} popup
 * @returns {object | null}
 */
export function findAiBannerSection(popup) {
  const steps = popup?.schema;
  if (!Array.isArray(steps)) return null;
  for (const step of steps) {
    const sections = step?.section;
    if (!Array.isArray(sections)) continue;
    const hit = sections.find(
      (s) => s?.type === "TEXT" && s?.setting?.role === "aiBanner"
    );
    if (hit) return hit;
  }
  return null;
}

/**
 * @param {object} popup
 * @returns {object | null}
 */
export function findFirstOfferProduct(popup) {
  const steps = popup?.schema;
  if (!Array.isArray(steps)) return null;
  for (const step of steps) {
    const sections = step?.section;
    if (!Array.isArray(sections)) continue;
    const productSec = sections.find((s) => s?.type === "PRODUCT");
    const p = productSec?.products?.[0];
    if (p) return p;
  }
  return null;
}

function summarizeProduct(p) {
  if (!p) return "unknown";
  const parts = [p.title, p.product_type || p.type, p.vendor].filter(Boolean);
  return parts.join(" · ") || String(p.handle || p.id || "product");
}

function summarizeCart(cart) {
  if (!cart) return "empty";
  const count = cart.itemCount ?? cart.items?.length ?? 0;
  const total = cart.total;
  const titles = (cart.items || [])
    .map((i) => i?.title || i?.product_title)
    .filter(Boolean)
    .slice(0, 5);
  const totalStr =
    typeof total === "number" ? ` total ${(total / 100).toFixed(2)}` : "";
  return `${count} item(s)${totalStr}${titles.length ? `: ${titles.join(", ")}` : ""}`;
}

/**
 * Parse `document.cookie` (or equivalent) into a plain object.
 * @param {string} cookieString
 * @returns {Record<string, string>}
 */
export function parseCookieString(cookieString) {
  const out = {};
  const raw = String(cookieString || "").trim();
  if (!raw) return out;
  const parts = raw.split(";");
  for (const part of parts) {
    const idx = part.indexOf("=");
    if (idx < 1) continue;
    const key = part.slice(0, idx).trim();
    const value = part.slice(idx + 1).trim();
    if (key) out[key] = decodeURIComponent(value.replace(/\+/g, " "));
  }
  return out;
}

/**
 * Collect browsing signals from localStorage keys and optional cookies.
 * Used by storefront (hybrid), admin editor preview, and tests.
 *
 * @param {unknown} storageKeys
 * @param {{ localStorage?: { getItem: (k: string) => string | null }, cookieString?: string }} [deps]
 */
export function collectBrowsingContext(storageKeys, deps = {}) {
  let keys = normalizeStorageKeys(storageKeys);
  if (!keys.length) {
    keys = [...DEFAULT_AI_RECOMMENDATION_STORAGE_KEYS];
  }
  const out = {};
  const ls = deps.localStorage;
  const ss = deps.sessionStorage;

  if (ls && keys.length) {
    for (const key of keys) {
      try {
        const raw = ls.getItem(key);
        if (!raw) continue;
        try {
          out[key] = JSON.parse(raw);
        } catch {
          out[key] = raw;
        }
      } catch {
        /* ignore */
      }
    }
  }

  if (ss && keys.length) {
    for (const key of keys) {
      try {
        const raw = ss.getItem(key);
        if (!raw) continue;
        const storageKey = `session:${key}`;
        try {
          out[storageKey] = JSON.parse(raw);
        } catch {
          out[storageKey] = raw;
        }
      } catch {
        /* ignore */
      }
    }
  }

  const cookieString = deps.cookieString;
  if (cookieString) {
    const cookies = parseCookieString(cookieString);
    if (Object.keys(cookies).length) {
      out._cookies = cookies;
    }
  }

  if (deps.shopifyCustomer && typeof deps.shopifyCustomer === "object") {
    out._shopifyCustomer = deps.shopifyCustomer;
  }

  return out;
}

/**
 * Resolve a display name from browsing signals, cookies, localStorage, or Shopify customer.
 * @param {object} [browsing]
 * @param {object} [shopperExtras] e.g. { shopifyCustomer: { first_name, last_name } }
 * @returns {string}
 */
export function extractShopperDisplayName(browsing, shopperExtras = {}) {
  const candidates = [];

  const pushName = (value) => {
    const name = normalizePersonName(value);
    if (name) candidates.push(name);
  };

  const shopifyCustomer =
    shopperExtras.shopifyCustomer || browsing?._shopifyCustomer;
  if (shopifyCustomer && typeof shopifyCustomer === "object") {
    if (shopifyCustomer.first_name) {
      pushName(
        shopifyCustomer.last_name
          ? `${shopifyCustomer.first_name} ${shopifyCustomer.last_name}`
          : shopifyCustomer.first_name
      );
    }
    pushName(shopifyCustomer.name);
    pushName(shopifyCustomer.displayName);
  }

  pushName(shopperExtras.name);
  pushName(shopperExtras.firstName);

  const cookies = browsing?._cookies;
  if (cookies && typeof cookies === "object") {
    if (cookies.first_name && cookies.last_name) {
      pushName(`${cookies.first_name} ${cookies.last_name}`);
    }
    for (const key of [
      "first_name",
      "firstName",
      "customer_first_name",
      "fname",
      "name",
      "customer_name",
      "display_name",
    ]) {
      pushName(cookies[key]);
    }
  }

  if (browsing && typeof browsing === "object") {
    for (const [key, val] of Object.entries(browsing)) {
      if (key === "_cookies" || key === "_shopifyCustomer") continue;
      pushName(extractNameFromStoredValue(val));
      if (/customer|user|profile|identity|account/i.test(key)) {
        pushName(extractNameFromStoredValue(val));
      }
    }
  }

  return candidates.find(Boolean) || "";
}

function normalizePersonName(value) {
  const text = String(value || "").trim();
  if (!text || text.length < 2 || text.length > 64) return "";
  if (/^(guest|customer|user|shopper|undefined|null)$/i.test(text)) return "";
  if (/^[\d@.+]+$/.test(text)) return "";
  return text.replace(/\s+/g, " ");
}

function extractNameFromStoredValue(value) {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return normalizePersonName(trimmed);
    }
    try {
      return extractNameFromStoredValue(JSON.parse(trimmed));
    } catch {
      return normalizePersonName(trimmed);
    }
  }
  if (typeof value !== "object") return "";

  if (value.first_name && value.last_name) {
    return normalizePersonName(`${value.first_name} ${value.last_name}`);
  }
  for (const key of [
    "first_name",
    "firstName",
    "name",
    "full_name",
    "fullName",
    "displayName",
    "customer_name",
  ]) {
    const hit = normalizePersonName(value[key]);
    if (hit) return hit;
  }

  for (const nested of Object.values(value)) {
    if (nested && typeof nested === "object") {
      const hit = extractNameFromStoredValue(nested);
      if (hit) return hit;
    }
  }
  return "";
}

/**
 * Build runtime/preview context for AI copy — real browser signals when available,
 * with sensible mock cart/product fallbacks for admin iframe preview.
 *
 * @param {object} popupData
 * @param {object} [options]
 */
export function buildPreviewAiRecommendationContext(popupData, options = {}) {
  const offerProduct =
    options.offerProduct || findFirstOfferProduct(popupData) || null;
  const productCond = popupData?.generalSettings?.condition?.product;
  const triggerProduct = productCond?.trigger_product;

  const storageKeys = options.storageKeys ?? [];
  const browsingFromBrowser =
    typeof window !== "undefined"
      ? collectBrowsingContext(storageKeys, {
          localStorage: window.localStorage,
          sessionStorage: window.sessionStorage,
          cookieString: typeof document !== "undefined" ? document.cookie : "",
          shopifyCustomer: window.Shopify?.customer,
        })
      : {};

  const page =
    typeof window !== "undefined"
      ? {
          path: window.location.pathname,
          url: window.location.href,
          referrer: document.referrer || "",
          title: document.title || "",
        }
      : {
          path: "/products/example",
          url: "",
          referrer: "",
          title: "",
        };

  const shopperName = extractShopperDisplayName(browsingFromBrowser, {
    shopifyCustomer: typeof window !== "undefined" ? window.Shopify?.customer : null,
  });

  const realCart =
    options.cart ||
    (typeof window !== "undefined" ? window.__weupsellCart : null) ||
    null;

  const realCurrentProduct =
    options.currentProduct ||
    (typeof window !== "undefined" ? window.__weupsellProduct : null) ||
    null;

  return {
    cart:
      realCart ||
      options.mockCart || {
        itemCount: 2,
        total: 8900,
        items: [
          { title: triggerProduct?.title || "Cart item A", quantity: 1 },
          { title: "Cart item B", quantity: 1 },
        ],
      },
    currentProduct: realCurrentProduct || triggerProduct || null,
    offerProduct,
    page: options.page || page,
    browsing: options.browsing ?? browsingFromBrowser,
    shopperName: options.shopperName || shopperName || undefined,
  };
}
